import { Box, Text } from '@user-portal/wikr-components';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { selectCurrentUser } from 'store/user/selectors';

import { useSelector } from 'hooks';

import { Cross, MoveBack } from 'components/Icon';
import Modal from 'components/Modal';
import OutlinedButton from 'components/OutlinedButton';

import { UserStore } from 'types/store/userStore';

import chair_plan_back_knees_plan from './img/chair_plan_back_knees_plan.jpg';
import excess_weight_plan from './img/excess_weight_plan.jpg';
import gym_strength_plan from './img/gym_strength_plan.jpg';
import { PlanSuggestionOfferModalProps } from './types';

import * as S from './styled';

const plans = [
    {
        shortKey: 'planExcess',
        type: 'excess_weight_plan',
        image: excess_weight_plan,
    },
    {
        shortKey: 'planGym',
        type: 'gym_strength_plan',
        image: gym_strength_plan,
    },
    {
        maleOnly: true,
        shortKey: 'planChair',
        type: 'chair_plan_back_knees_plan',
        image: chair_plan_back_knees_plan,
    },
];

const PlanSuggestionOfferModal = ({
    onApply,
    onMoveBack,
    onClose,
    onCancel,
    trackLoad,
}: PlanSuggestionOfferModalProps) => {
    const { t } = useTranslation();
    const currentUser = useSelector(selectCurrentUser) as UserStore;

    const planFiltered = useMemo(
        () =>
            plans.filter(({ maleOnly }) => {
                return currentUser.gender === 'male' ? true : !maleOnly;
            }),
        [currentUser]
    );

    return (
        <Modal
            isOpen
            paddingX={0}
            paddingY={0}
            onClose={onClose}
            transparentBackground
            transparentOverlay={false}
            trackLoad={trackLoad}
        >
            <S.ModalContent>
                <S.ModalHeaderTitle>
                    <S.IconWrapper
                        onClick={onMoveBack}
                        data-testid="cancelSubscriptionModalBackButton"
                        data-locator="cancelSubscriptionModalBackButton"
                    >
                        <MoveBack />
                    </S.IconWrapper>
                    <S.TitleText>
                        <Text type="large-text" medium>
                            {t('subscription.text.cancel')}
                        </Text>
                    </S.TitleText>
                    <S.IconWrapper
                        onClick={onClose}
                        data-testid="cancelSubscriptionModalCloseButton"
                        data-locator="cancelSubscriptionModalCloseButton"
                    >
                        <Cross />
                    </S.IconWrapper>
                </S.ModalHeaderTitle>
                <Text type="h4" medium center mb={25}>
                    {t('subscription.planSuggestion.text')}
                </Text>
                <Box>
                    {planFiltered.map(({ type, image, shortKey }) => (
                        <S.CardItem key={type}>
                            <S.CardImageWrapper>
                                <S.Image src={image} alt={type} />
                            </S.CardImageWrapper>
                            <S.TextWrapper>
                                <Box $mb={15}>
                                    <Text color="text-secondary" type="caption" mb={5}>
                                        {t(`subscription.planSuggestion.${shortKey}.caption`)}
                                    </Text>
                                    <Text type="h5" medium mb={10}>
                                        {t(`subscription.planSuggestion.${shortKey}.title`)}
                                    </Text>
                                    <Text color="text-secondary" type="small-text">
                                        {t(`subscription.planSuggestion.${shortKey}.subtitle`)}
                                    </Text>
                                </Box>
                                <S.SmallButton size="small" onClick={() => onApply(type)}>
                                    {t('account.settings.button.tryPlan')}
                                </S.SmallButton>
                            </S.TextWrapper>
                        </S.CardItem>
                    ))}
                    <S.CardItem small>
                        <S.TextWrapper>
                            <Box $mb={15}>
                                <Text type="large-text" uppercase medium mb={10}>
                                    {t('subscription.planSuggestion.custom.title')}
                                </Text>
                                <Text color="text-secondary" type="small-text" mb={60}>
                                    {t('subscription.planSuggestion.custom.subtitle')}
                                </Text>
                            </Box>
                            <S.SmallButton size="small" onClick={() => onApply(null)}>
                                {t('account.settings.button.customizePlan')}
                            </S.SmallButton>
                        </S.TextWrapper>
                    </S.CardItem>
                </Box>
                <OutlinedButton onClick={onCancel} textColor="action-main">
                    {t('subscription.text.cancel')}
                </OutlinedButton>
            </S.ModalContent>
        </Modal>
    );
};

export default PlanSuggestionOfferModal;
